import { ComponentProps, FC } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { textVariants } from "./RmText/variants";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border px-2 py-0.5 whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/70 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        warning:
          "border-transparent bg-amber-400 text-amber-950 [a&]:hover:bg-amber-400/90",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type RmBadgePropsType = ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> &
  ChildrenOrI18nType & {
    asChild?: boolean;
  };

export const RmBadge: FC<RmBadgePropsType> = ({
  className,
  variant,
  i18nKey,
  i18nOptions,
  children,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot : "span";
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <Comp
      data-slot="badge"
      className={cn(
        textVariants({
          variant: "sm",
          weight: "bold",
        }),
        badgeVariants({ variant, className }),
      )}
      {...props}>
      {children ?? i18nText}
    </Comp>
  );
};
